import { useEffect, useMemo, useState } from 'react';
import { showcaseAPI, productsAPI, storesAPI, uploadToCloudinary } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Sparkles, ArrowUp, ArrowDown, ExternalLink,
  Package, Store, Link2, Film, Image as ImageIcon, Upload, Loader2,
} from 'lucide-react';

const CAMPAIGN_OPTIONS = ['', 'Oferta', 'Novidade', 'Destaque', 'Exclusivo'];

const INITIAL = {
  order: 0,
  active: true,
  link_type: 'product',
  product_id: '',
  store_id: '',
  external_url: '',
  media_url: '',
  media_type: 'image',
  custom_title: '',
  custom_subtitle: '',
  campaign_label: '',
};

export const DynamicShowcaseAdmin = () => {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reordering, setReordering] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, prodRes, storeRes] = await Promise.all([
        showcaseAPI.adminList(),
        productsAPI.list({}),
        storesAPI.list({}),
      ]);
      setItems(data.items || []);
      setProducts(prodRes.data || []);
      setStores(storeRes.data || []);
    } catch (e) {
      toast.error('Erro ao carregar vitrine');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...INITIAL, order: items.length });
    setDialogOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      order: it.order ?? 0,
      active: !!it.active,
      link_type: it.link_type || 'product',
      product_id: it.product_id || '',
      store_id: it.store_id || '',
      external_url: it.external_url || '',
      media_url: it.media_url || '',
      media_type: it.media_type || 'image',
      custom_title: it.custom_title || '',
      custom_subtitle: it.custom_subtitle || '',
      campaign_label: it.campaign_label || '',
    });
    setDialogOpen(true);
  };

  const handleMediaUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const resourceType = form.media_type === 'video' ? 'video' : 'image';
      const result = await uploadToCloudinary(file, resourceType, 'showcase');
      if (result.secure_url) {
        setForm((s) => ({ ...s, media_url: result.secure_url }));
        toast.success('Mídia enviada!');
      }
    } catch (e) {
      toast.error('Falha no upload');
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.link_type === 'product' && !form.product_id) {
      toast.error('Selecione um produto'); return;
    }
    if (form.link_type === 'store' && !form.store_id) {
      toast.error('Selecione uma loja'); return;
    }
    if (form.link_type === 'external' && (!form.external_url || !form.media_url)) {
      toast.error('Informe URL externa e mídia'); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        product_id: form.link_type === 'product' ? form.product_id : null,
        store_id: form.link_type === 'store' ? form.store_id : null,
        external_url: form.link_type === 'external' ? form.external_url : '',
        order: Number(form.order) || 0,
      };
      if (editing) {
        await showcaseAPI.update(editing.id, payload);
        toast.success('Item atualizado!');
      } else {
        await showcaseAPI.create(payload);
        toast.success('Item adicionado à vitrine!');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Excluir o item da vitrine?`)) return;
    try {
      await showcaseAPI.delete(it.id);
      toast.success('Item removido');
      load();
    } catch (e) {
      toast.error('Erro ao excluir');
    }
  };

  const toggleActive = async (it) => {
    try {
      await showcaseAPI.update(it.id, { active: !it.active });
      load();
    } catch (e) {
      toast.error('Erro ao alterar status');
    }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const reordered = [...items];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(target, 0, moved);
    setItems(reordered);
    setReordering(true);
    try {
      await showcaseAPI.reorder(reordered.map((it) => it.id));
      load();
    } catch (e) {
      toast.error('Erro ao reordenar');
      load();
    } finally {
      setReordering(false);
    }
  };

  const productOptions = useMemo(
    () => products.map((p) => ({ id: p.id, label: `${p.name} — ${p.store_name || ''}` })),
    [products]
  );
  const storeOptions = useMemo(
    () => stores.map((s) => ({ id: s.id, label: s.name })),
    [stores]
  );

  const displayMedia = (it) => {
    if (it.media_url) return { url: it.media_url, type: it.media_type || 'image' };
    if (it.link_type === 'product' && it.product_id) {
      const p = products.find((x) => x.id === it.product_id);
      if (p) return { url: p.video_url || (p.images || [])[0] || '', type: p.video_url ? 'video' : 'image' };
    }
    if (it.link_type === 'store' && it.store_id) {
      const s = stores.find((x) => x.id === it.store_id);
      if (s) return { url: s.banner_url || s.logo_url || '', type: 'image' };
    }
    return { url: '', type: it.media_type || 'image' };
  };

  return (
    <div className="space-y-6" data-testid="showcase-admin-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-blue-600" />
            Vitrine Dinâmica
          </h1>
          <p className="text-slate-500 mt-1">
            Cure o carrossel autoplay da home. Ordene, ative/desative, destaque campanhas e alterne produtos, lojas e imagens/vídeos.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="add-showcase-btn">
          <Plus className="w-4 h-4 mr-2" /> Novo destaque
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 py-16 text-center text-slate-500" data-testid="showcase-empty">
          <Sparkles className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          <p className="font-medium">Nenhum destaque cadastrado ainda.</p>
          <p className="text-sm mt-1">Clique em <strong>Novo destaque</strong> para começar.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="showcase-list">
          {items.map((it, i) => {
            const media = displayMedia(it);
            return (
            <Card key={it.id} className={`overflow-hidden ${!it.active ? 'opacity-70' : ''}`} data-testid={`showcase-item-${it.id}`}>
              <div className="relative aspect-[16/10] bg-slate-100">
                {media.type === 'video' && media.url ? (
                  <video src={media.url} muted className="w-full h-full object-cover">
                    <track kind="captions" />
                  </video>
                ) : media.url ? (
                  <img src={media.url} alt={it.custom_title || it.target_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-300">
                    <ImageIcon className="w-10 h-10" />
                  </div>
                )}
                {it.campaign_label && (
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest text-white bg-blue-600 shadow">
                    {it.campaign_label}
                  </span>
                )}
                <div className="absolute top-2 right-2 flex gap-1">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest text-white ${it.active ? 'bg-emerald-500' : 'bg-slate-400'}`}>
                    {it.active ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              </div>
              <CardContent className="p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      {it.link_type === 'product' && <Package className="w-3 h-3" />}
                      {it.link_type === 'store' && <Store className="w-3 h-3" />}
                      {it.link_type === 'external' && <Link2 className="w-3 h-3" />}
                      {it.link_type}
                    </div>
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {it.custom_title || it.target_name || '(sem título)'}
                    </p>
                    {it.custom_subtitle && (
                      <p className="text-xs text-slate-500 truncate">{it.custom_subtitle}</p>
                    )}
                  </div>
                  <div className="text-right text-xs text-slate-400 font-mono">#{it.order}</div>
                </div>

                <div className="flex flex-wrap gap-1 pt-1">
                  <Button size="sm" variant="outline" onClick={() => move(i, -1)} disabled={i === 0 || reordering} data-testid={`move-up-${it.id}`}>
                    <ArrowUp className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => move(i, 1)} disabled={i === items.length - 1 || reordering} data-testid={`move-down-${it.id}`}>
                    <ArrowDown className="w-3.5 h-3.5" />
                  </Button>
                  <div className="flex items-center gap-1 px-2">
                    <Switch checked={it.active} onCheckedChange={() => toggleActive(it)} data-testid={`toggle-active-${it.id}`} />
                    <Label className="text-xs">Ativo</Label>
                  </div>
                  <div className="ml-auto flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => openEdit(it)} data-testid={`edit-showcase-${it.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(it)} data-testid={`delete-showcase-${it.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar destaque' : 'Novo destaque'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            {/* Tipo de link */}
            <div className="space-y-2">
              <Label>Tipo de link *</Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'product', label: 'Produto', icon: Package },
                  { key: 'store',   label: 'Loja',    icon: Store   },
                  { key: 'external',label: 'Externo', icon: Link2   },
                ].map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setForm((s) => ({ ...s, link_type: key }))}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${
                      form.link_type === key
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white border-slate-200 text-slate-700 hover:border-blue-300'
                    }`}
                    data-testid={`link-type-${key}`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </button>
                ))}
              </div>
            </div>

            {form.link_type === 'product' && (
              <div className="space-y-2">
                <Label>Produto *</Label>
                <Select value={form.product_id} onValueChange={(v) => setForm((s) => ({ ...s, product_id: v }))}>
                  <SelectTrigger data-testid="select-product"><SelectValue placeholder="Selecione um produto" /></SelectTrigger>
                  <SelectContent>
                    {productOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Se você não enviar mídia customizada, a vitrine usará a primeira imagem/vídeo do produto.</p>
              </div>
            )}
            {form.link_type === 'store' && (
              <div className="space-y-2">
                <Label>Loja *</Label>
                <Select value={form.store_id} onValueChange={(v) => setForm((s) => ({ ...s, store_id: v }))}>
                  <SelectTrigger data-testid="select-store"><SelectValue placeholder="Selecione uma loja" /></SelectTrigger>
                  <SelectContent>
                    {storeOptions.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.link_type === 'external' && (
              <div className="space-y-2">
                <Label>URL externa *</Label>
                <Input
                  type="url"
                  value={form.external_url}
                  onChange={(e) => setForm((s) => ({ ...s, external_url: e.target.value }))}
                  placeholder="https://..."
                  data-testid="input-external-url"
                />
              </div>
            )}

            {/* Mídia */}
            <div className="space-y-2 border-t pt-4">
              <Label className="flex items-center gap-2">
                {form.media_type === 'video' ? <Film className="w-4 h-4 text-blue-600" /> : <ImageIcon className="w-4 h-4 text-blue-600" />}
                Mídia
              </Label>
              <div className="flex gap-2">
                {[
                  { key: 'image', label: 'Imagem' },
                  { key: 'video', label: 'Vídeo' },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setForm((s) => ({ ...s, media_type: key }))}
                    className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                      form.media_type === key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200 text-slate-700'
                    }`}
                    data-testid={`media-type-${key}`}
                  >{label}</button>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  value={form.media_url}
                  onChange={(e) => setForm((s) => ({ ...s, media_url: e.target.value }))}
                  placeholder="URL da mídia (opcional se for produto)"
                  className="flex-1"
                  data-testid="input-media-url"
                />
                <input
                  type="file"
                  accept={form.media_type === 'video' ? 'video/*' : 'image/*'}
                  onChange={(e) => handleMediaUpload(e.target.files?.[0])}
                  className="hidden"
                  id="showcase-media-upload"
                  disabled={uploading}
                />
                <label
                  htmlFor="showcase-media-upload"
                  className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-slate-200 text-sm cursor-pointer hover:bg-slate-50 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                  data-testid="upload-showcase-media-btn"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Enviar
                </label>
              </div>
              {form.media_url && (
                form.media_type === 'video' ? (
                  <video src={form.media_url} controls className="w-full max-h-48 rounded-md bg-black">
                    <track kind="captions" />
                  </video>
                ) : (
                  <img src={form.media_url} alt="preview" className="w-full max-h-48 object-cover rounded-md" />
                )
              )}
            </div>

            {/* Textos e campanha */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border-t pt-4">
              <div className="space-y-2">
                <Label>Título (opcional)</Label>
                <Input
                  value={form.custom_title}
                  onChange={(e) => setForm((s) => ({ ...s, custom_title: e.target.value }))}
                  placeholder="Sobrescreve o nome do produto/loja"
                  data-testid="input-custom-title"
                />
              </div>
              <div className="space-y-2">
                <Label>Subtítulo (opcional)</Label>
                <Input
                  value={form.custom_subtitle}
                  onChange={(e) => setForm((s) => ({ ...s, custom_subtitle: e.target.value }))}
                  placeholder="Ex.: Frete grátis / Chegando agora"
                  data-testid="input-custom-subtitle"
                />
              </div>
              <div className="space-y-2">
                <Label>Selo de campanha</Label>
                <Select value={form.campaign_label || 'none'} onValueChange={(v) => setForm((s) => ({ ...s, campaign_label: v === 'none' ? '' : v }))}>
                  <SelectTrigger data-testid="select-campaign"><SelectValue placeholder="Nenhum" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum</SelectItem>
                    {CAMPAIGN_OPTIONS.filter(Boolean).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ordem</Label>
                <Input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm((s) => ({ ...s, order: e.target.value }))}
                  data-testid="input-order"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 border-t pt-4">
              <Switch checked={form.active} onCheckedChange={(v) => setForm((s) => ({ ...s, active: v }))} data-testid="switch-active" />
              <Label>Ativo na home</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={submitting} data-testid="submit-showcase-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editing ? 'Salvar' : 'Adicionar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DynamicShowcaseAdmin;
